import dayjs from 'dayjs';
import getReadings from '../dataServices';
import {
  SoilMoistureReading,
  FormattedSeries,
  LineReading,
  PlantReading,
  FormattedReading,
  StandardLineReading,
} from './types';
import { ChartFiltersWiseconn } from '../types';

const groupDataBy = <T>(
  data: T[],
  getGroupKey: (item: T) => string,
  defaultKey = '',
): Record<string, T[]> => data.reduce((acc: Record<string, T[]>, item: T) => {
    const key = getGroupKey(item) || defaultKey;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

const getGroupKey = (
  sensorType: string,
) => (item: LineReading): { item: string; name: string } => {
  switch (sensorType) {
    case 'Soil Moisture':
    case 'Salinity':
    case 'Soil Temperature':
      return { item: (item as SoilMoistureReading).sensorDepth, name: 'depth' };
    case 'Plant Caliper':
      return { item: (item as PlantReading).name, name: 'name' };
    case 'Temperature':
      return { item: (item as StandardLineReading).sensorType, name: 'sensor' };
    case 'Humidity':
      return { item: (item as StandardLineReading).sensorType, name: 'sensor' };
    case 'Fruit Size':
      return { item: (item as PlantReading).name, name: 'name' };
    case 'pH':
      return { item: (item as StandardLineReading).sensorType, name: 'sensor' };
    case 'Irrigation Electrical Conductivity':
      return { item: (item as StandardLineReading).sensorType, name: 'sensor' };
    case 'Wind Velocity':
      return { item: (item as StandardLineReading).sensorType, name: 'sensor' };
    case 'Solar Radiation':
      return { item: (item as StandardLineReading).sensorType, name: 'sensor' };
    default:
      throw new Error(`Unsupported sensor type: ${sensorType}`);
  }
};

const prepareData = async (filters: ChartFiltersWiseconn, telemetryType?: string) => {
  if (!filters.sensorType) {
    return [];
  }
  // Ejecutar llamada API para datos
  const readingData = await getReadings({
    ...filters,
    granularity: 'all',
    telemetryType: telemetryType || filters.telemetryType,
  });

  if (!readingData || readingData.length === 0) {
    return [];
  }

  const groupKey = getGroupKey(filters.sensorType);

  // Group by depth and transform the data
  const groupedByDepth = groupDataBy<LineReading>(
    readingData,
    (item) => groupKey(item).item,
  );

  // Transform into the new format
  const formattedSeries: FormattedSeries[] = Object.entries(
    groupedByDepth,
  ).map(([depth, readings]: [string, LineReading[]]) => {
    const firstReading = readings[0];
    const groupKeyResult = groupKey(firstReading);

    // Sort readings by date
    const sortedReadings = readings.sort(
      (a: LineReading, b: LineReading) => dayjs(a.dateTime).diff(dayjs(b.dateTime)),
    );

    let seriesName = '';
    if (filters.sensorType === 'Plant Caliper') {
      seriesName = filters.name || firstReading.sensorType;
    } else if (filters.sensorType === 'Fruit Size') {
      seriesName = 'Dendrómetro Fruto CD';
    } else if (groupKeyResult.name === 'depth') {
      seriesName = `${depth} ${(firstReading as SoilMoistureReading).depthUnit} (${(firstReading as SoilMoistureReading).unit})`;
    } else if (groupKeyResult.name === 'sensor') {
      seriesName = `${firstReading.sensorType} (${firstReading.unit})`;
    }

    const result = {
      ...(groupKeyResult.name === 'depth' && {
        sensorDepth: depth,
        depthUnit: (firstReading as SoilMoistureReading).depthUnit,
      }),
      ...(groupKeyResult.name === 'sensor' && {
        name: firstReading.sensorType,
      }),
      unit: firstReading.unit,
      sensorType: firstReading.sensorType,
      name: seriesName,
      data: sortedReadings.map((reading: LineReading) => ({
        dateTime: dayjs(reading.dateTime).format('YYYY-MM-DD HH:mm'),
        value: reading.value,
      })),
    };
    return result;
  });

  return formattedSeries;
};

const calculateCategories = (
  startDate: string, endDate: string,
) => {
  const start = dayjs(startDate).subtract(4, 'hours');
  const end = dayjs(endDate);
  const allDates: string[] = [];

  let current = start;
  while (current.isBefore(end) || current.isSame(end)) {
    allDates.push(current.format('YYYY-MM-DD HH:mm'));
    current = current.add(15, 'minute');
  }

  return allDates;
};
const calculateSeriesOffset = (
  formattedSeries: {
    type: string;
    name: string;
    data: { y: number, x: number }[];
    sensorDepth?: string;
  }[],
  shouldShowOriginal: boolean,
) => {
  if (shouldShowOriginal) {
    let globalMinY = Infinity;
    let globalMaxY = -Infinity;

    formattedSeries.forEach((series) => {
      series.data.forEach((reading: { y: number, x: number }) => {
        globalMinY = Math.min(globalMinY, reading.y);
        globalMaxY = Math.max(globalMaxY, reading.y);
      });
    });

    return {
      series: formattedSeries,
      min: globalMinY === Infinity ? 0 : globalMinY,
      max: globalMaxY === -Infinity ? 1 : globalMaxY,
    };
  }

  const seriesToProcess = [...formattedSeries].sort((a, b) => {
    const depthA = a.sensorDepth ? parseFloat(a.sensorDepth) : 0;
    const depthB = b.sensorDepth ? parseFloat(b.sensorDepth) : 0;
    return depthB - depthA;
  });

  const OFFSET = 0.5;
  let globalMinY = Infinity;
  let globalMaxY = -Infinity;
  let previousMaxY = 0;

  const seriesWithOffset = seriesToProcess.map((series) => {
    let seriesMinY = Infinity;
    let seriesMaxY = -Infinity;

    series.data.forEach((reading: { y: number, x: number }) => {
      seriesMinY = Math.min(seriesMinY, reading.y);
      seriesMaxY = Math.max(seriesMaxY, reading.y);
    });

    const transformedData = series.data.map((reading: { y: number, x: number }) => {
      const newY = reading.y - seriesMinY + previousMaxY + OFFSET;
      globalMinY = Math.min(globalMinY, newY);
      globalMaxY = Math.max(globalMaxY, newY);
      return {
        ...reading,
        y: newY,
      };
    });

    const currentMaxY = seriesMaxY - seriesMinY + previousMaxY + OFFSET;
    previousMaxY = currentMaxY;

    return {
      ...series,
      data: transformedData,
    };
  });

  return {
    series: seriesWithOffset.reverse(),
    min: globalMinY === Infinity ? 0 : globalMinY,
    max: globalMaxY === -Infinity ? 1 : globalMaxY,
  };
};

const calculateSeries = (
  preparedData: FormattedSeries[],
  shouldShowOriginal: boolean,
  displayStartDate: string,
  displayEndDate: string,
) => {
  const initialSeriesData = preparedData.map((series) => ({
    type: 'line',
    name: series.name || '',
    sensorDepth: series.sensorDepth,
    data: series.data.map((reading: FormattedReading) => {
      const timestamp = dayjs(reading.dateTime).utc().valueOf();
      return {
        x: timestamp,
        y: reading.value,
        custom: {
          date: reading.dateTime,
          realValue: reading.value,
          exportValue: reading.value,
        },
      };
    }),
  }));

  let seriesData;

  if (shouldShowOriginal) {
    seriesData = initialSeriesData;
  } else {
    const seriesToProcess = [...initialSeriesData].sort((a, b) => {
      const depthA = a.sensorDepth ? parseFloat(a.sensorDepth) : 0;
      const depthB = b.sensorDepth ? parseFloat(b.sensorDepth) : 0;
      return depthB - depthA;
    });

    const OFFSET = 0.5;
    let previousMaxY = 0;

    const seriesWithOffset = seriesToProcess.map((series) => {
      let seriesMinY = Infinity;
      let seriesMaxY = -Infinity;

      series.data.forEach((reading) => {
        seriesMinY = Math.min(seriesMinY, reading.y);
        seriesMaxY = Math.max(seriesMaxY, reading.y);
      });

      const transformedData = series.data.map((reading) => {
        const newY = reading.y - seriesMinY + previousMaxY + OFFSET;
        return {
          ...reading,
          y: newY,
        };
      });

      const currentMaxY = seriesMaxY - seriesMinY + previousMaxY + OFFSET;
      previousMaxY = currentMaxY;

      return {
        ...series,
        data: transformedData,
      };
    });

    seriesData = seriesWithOffset.reverse();
  }

  const allDates = calculateCategories(displayStartDate, displayEndDate);

  return {
    series: seriesData,
    absoluteCategories: allDates,
    hideYValues: !shouldShowOriginal,
  };
};

export {
  prepareData, calculateCategories, calculateSeries, calculateSeriesOffset,
};
